import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  useMediaQuery,
} from '@mui/material';

// COMPONENTS
import CountrySelect from '../../common/inputs/CountrySelect';
import HelpTooltip from '../../common/HelpTooltip';
import LanguagePicker from '../../common/LanguagePicker';
import ManageDedicatedNumbers from '../../common/management/ManageDedicatedNumbers';
import PasswordRequirements from '../../common/PasswordRequirements';
import ErrorList from '../../common/ErrorList';

// MESSAGES
import UsersFormattedMessages from '../../../messages/account/UsersFormattedMessages';

// REQUESTS
import useUserRequest from '../../../requests/UsersRequest';

// UTILS
import {
  validateEmail,
  validatePassword,
  validatePhoneNumber,
  isE164Format,
} from '../../../utils/validate';

const NewUserEditForm = ({
  onClose,
  errorHandler,
  accessLevel,
  accessType,
  user,
  manageDedicatedNumbers,
  setManageDedicatedNumbers,
}) => {
  const { getCentres, getDedicatedNumbers, updateUser, createUser } =
    useUserRequest();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const mdAndUp = useMediaQuery((theme) => theme.breakpoints.up('md'));

  const [formState, setFormState] = useState({
    centres: [],
    dns: [],
    validPassword: true,
    validConfirmPassword: true,
    isSaving: false,
    showConfirmation: false,
    userStatus: 'C',
    contactStatus: '',
    passwordTouched: false,
    confirmPasswordTouched: false,
  });

  const [formData, setFormData] = useState({
    user_name: '',
    password: '',
    password_check: '',
    country_id: '',
    language_id: '',
    cost_centre_id: '',
    max_sms_per_day: '25',
    reciept_flag: false,
    message_priority: '0',
    http_reply_flag: false,
    last_sms_text_flag: false,
    user_tn: '',
    user_type: 'S',
    user_email: '',
    forward_calls_to: '',
    is_forward: null,
  });

  const isGodAdmin = accessLevel === '3' && accessType === '3';

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        // When creating a new user, we only need centres
        const isNewUser = !user?.customer_user_id;
        const [centresData, dedicatedNumbers] = await Promise.all([
          getCentres(errorHandler),
          // Only fetch dedicated numbers for existing users
          isNewUser
            ? Promise.resolve([])
            : getDedicatedNumbers(user.customer_user_id, errorHandler),
        ]);

        setFormState((prev) => ({
          ...prev,
          centres: centresData || [],
          dns: dedicatedNumbers || [],
        }));
      } catch (error) {
        console.error('Error loading form data:', error);
        setGeneralError('Error loading form data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.customer_user_id]);

  // Update form data when user changes
  useEffect(() => {
    if (!user) return;

    setFormData({
      user_name: user.user_name || '',
      password: '',
      password_check: '',
      country_id: user.country_id || '',
      language_id: user.language_id || '',
      cost_centre_id: user.cost_centre_id || '',
      max_sms_per_day: user.max_sms_per_day || '',
      reciept_flag: user.reciept_flag === 'Y',
      message_priority: user.message_priority || '0',
      http_reply_flag: user.http_reply_flag === 'Y',
      last_sms_text_flag: user.last_sms_text_flag === 'Y',
      user_tn: user.user_tn || '',
      user_type: user.user_type || 'S',
      user_email: user.user_email || '',
      forward_calls_to: user.forward_calls_to || '',
      is_forward: user.forward_calls_to ? true : false,
    });
  }, [user]);

  const handleChange = (field) => (event) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === 'is_forward' && !value) {
      setFormData((prev) => ({
        ...prev,
        forward_calls_to: '',
      }));
    }
    // Update touched state for password fields
    if (field === 'password') {
      setFormState((prev) => ({
        ...prev,
        passwordTouched: true,
        validPassword: !value || validatePassword(value),
        validConfirmPassword: !value || value === formData.password_check,
      }));
    }

    if (field === 'password_check') {
      setFormState((prev) => ({
        ...prev,
        confirmPasswordTouched: true,
        validConfirmPassword: !formData.password || value === formData.password,
      }));
    }
  };

  /**
   * generates the user info for godadmin.
   * @returns JSX for the user info.
   */
  const generateUserInfo = () => {
    if (accessLevel === '3' && accessType === '3' && user) {
      return (
        <Grid sx={12}>
          <Stack sx={{ gap: 2 }}>
            <Typography>
              Type - Class - Status:{' '}
              {`${user.user_type || ''} - ${user.user_class || ''} - ${
                user.user_status || ''
              }`}
            </Typography>
            <Typography>
              Operating System Version: {`${user.os_version || ''}`}
            </Typography>
            <Typography>
              Mail Client Version: {`${user.mc_version || ''}`}
            </Typography>
            <Typography>
              Red Oxygen Software Release: {`${user.sw_release || ''}`}
            </Typography>
            <Typography>Timezone: {`${user.timezone || ''}`}</Typography>
            <Typography>
              Daylight Savings Flag:
              {`${user.dls_flag || ''}`}
            </Typography>
          </Stack>
        </Grid>
      );
    }
    return null;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.user_name) {
      newErrors.user_name = UsersFormattedMessages.errTextUserName;
    }

    if (!formData.user_email) {
      newErrors.user_email = UsersFormattedMessages.errTextEmail;
    } else if (!validateEmail(formData.user_email)) {
      newErrors.user_email = 'Invalid email format';
    }

    if (!formData.country_id) {
      newErrors.country_id = UsersFormattedMessages.errTextCountry;
    }

    if (!formData.language_id) {
      newErrors.language_id = UsersFormattedMessages.errTextLanguage;
    }

    if (!formData.max_sms_per_day) {
      newErrors.max_sms_per_day = UsersFormattedMessages.errBlankMaxSms;
    }

    // Password validation for new users
    const isNewUser = !user?.customer_user_id;
    if (isNewUser) {
      if (!formData.password) {
        newErrors.password = 'Password is required for new users';
      } else if (!validatePassword(formData.password)) {
        newErrors.password = UsersFormattedMessages.errCheckPasswordStrong;
      }

      if (formData.password !== formData.password_check) {
        newErrors.password_check = UsersFormattedMessages.errCheckPasswordMatch;
      }
    } else if (formData.password) {
      // For existing users, only validate if password is being changed
      if (!validatePassword(formData.password)) {
        newErrors.password = UsersFormattedMessages.errCheckPasswordStrong;
      }

      if (formData.password !== formData.password_check) {
        newErrors.password_check = UsersFormattedMessages.errCheckPasswordMatch;
      }
    }

    // Validate phone number if provided
    if (formData.user_tn && !validatePhoneNumber(formData.user_tn)) {
      newErrors.user_tn = UsersFormattedMessages.errCheckPhoneFormat;
    }
    if (formData.is_forward && !isE164Format(formData.forward_calls_to)) {
      newErrors.is_forward = UsersFormattedMessages.errForwardToNumberFormat;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return false;
    }

    try {
      setFormState((prev) => ({
        ...prev,
        isSaving: true,
      }));

      // Prepare data for API
      // Convert numeric fields and validate
      const max_sms_per_day = parseInt(formData.max_sms_per_day, 10);
      const message_priority = parseInt(formData.message_priority, 10);

      // Convert boolean flags to 'Y'/'N' strings and prepare user data
      const userData = {
        ...formData, // Include all form data, including password_check
        reciept_flag: formData.reciept_flag ? 'Y' : 'N',
        http_reply_flag: formData.http_reply_flag ? 'Y' : 'N',
        last_sms_text_flag: formData.last_sms_text_flag ? 'Y' : 'N',
        // Ensure these fields are included and properly formatted
        user_type: formData.user_type || user?.user_type || 'S', // Default to Standard user
        user_status: user?.user_status || 'A',
        // Handle numeric fields
        max_sms_per_day: isNaN(max_sms_per_day) ? undefined : max_sms_per_day,
        message_priority: isNaN(message_priority)
          ? undefined
          : message_priority,
      };

      let result;
      if (user?.customer_user_id) {
        // Update existing user
        result = await updateUser(
          user.customer_user_id,
          userData,
          errorHandler
        );
      } else {
        // Create new user
        result = await createUser(userData, errorHandler);
      }

      if (!result) {
        throw new Error(
          user?.customer_user_id
            ? 'Failed to update user'
            : 'Failed to create user'
        );
      }

      if (result.error) {
        throw new Error(result.error);
      }

      onClose(true);
    } catch (error) {
      console.error('Error saving user:', error);
      setGeneralError('Error saving user');
    } finally {
      setFormState((prev) => ({ ...prev, isSaving: false }));
    }
  };

  if (manageDedicatedNumbers) {
    return (
      <ManageDedicatedNumbers
        user={user}
        numbers={formState.dns}
        errorHandler={errorHandler}
        onClose={() => setManageDedicatedNumbers(false)}
      />
    );
  }

  return (
    <Grid container spacing={2} sx={{ width: '100%' }}>
      <Grid sx={{ maxWidth: { md: 500 } }} size={{ xs: 12, lg: 6 }}>
        <Stack spacing={3}>
          {user && (
            <Stack spacing={2}>
              <Typography variant="h2">{user.user_email}</Typography>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems="flex-start"
                justifyContent={'space-between'}
              >
                <Box sx={{ width: { md: 270 } }}>
                  <Typography
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 500,
                    }}
                  >
                    Dedicated Numbers
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  {formState.dns?.length > 0 ? (
                    formState.dns.map((number, index) => (
                      <Typography key={index}>{number.reply_tn}</Typography>
                    ))
                  ) : (
                    <Typography color="text.secondary">
                      No dedicated numbers assigned
                    </Typography>
                  )}
                </Stack>
              </Stack>
              {isGodAdmin && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    my: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={() => setManageDedicatedNumbers(true)}
                  >
                    Manage Dedicated Numbers
                  </Button>
                </Box>
              )}

              <Divider />
            </Stack>
          )}

          {/* User Name */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ md: 'center' }}
          >
            <InputLabel
              sx={{
                width: { md: 270 },
              }}
              required
            >
              User Name
            </InputLabel>

            <TextField
              fullWidth
              id="user-name"
              name="user_name"
              required
              value={formData.user_name}
              onChange={handleChange('user_name')}
              size="small"
              error={!!errors.user_name}
              helperText={errors.user_name}
            />
          </Stack>

          {/* Email */}
          {!user && (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1}
              alignItems={{ md: 'center' }}
            >
              <InputLabel
                sx={{
                  width: { md: 270 },
                }}
                required
              >
                Email
              </InputLabel>

              <TextField
                fullWidth
                name="user_email"
                id="user-email"
                required
                value={formData.user_email}
                onChange={handleChange('user_email')}
                size="small"
                error={!!errors.user_email}
                helperText={errors.user_email}
              />
            </Stack>
          )}

          {/* Mobile */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ md: 'center' }}
          >
            <InputLabel
              sx={{
                width: { md: 270 },
              }}
            >
              Mobile
            </InputLabel>

            <TextField
              fullWidth
              id="mobile"
              name="user_tn"
              value={formData.user_tn}
              onChange={handleChange('user_tn')}
              size="small"
              error={!!errors.user_tn}
              helperText={errors.user_tn}
            />
          </Stack>

          {/* Country */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ md: 'center' }}
          >
            <InputLabel
              sx={{
                width: { md: 270 },
              }}
              required
            >
              Country
            </InputLabel>

            <CountrySelect
              id="country"
              name="country_id"
              value={formData.country_id || ''}
              fullWidth
              selectDefault
              onChange={(_, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  country_id: newValue,
                }));
              }}
              error={!!errors.country_id}
              helperText={errors.country_id}
              errorHandler={errorHandler}
            />
          </Stack>

          {/* Language */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ md: 'center' }}
          >
            <InputLabel
              sx={{
                width: { md: 270 },
              }}
              required
            >
              Language
            </InputLabel>
            <LanguagePicker
              id="language"
              name="language_id"
              value={formData.language_id || ''}
              onChange={(value) => {
                setFormData((prev) => ({
                  ...prev,
                  language_id: value,
                }));
              }}
              size="small"
              selectDefault={true}
              error={!!errors.language_id}
              helperText={errors.language_id}
              errorHandler={errorHandler}
            />
          </Stack>

          {/* Cost Centers */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ md: 'center' }}
          >
            <InputLabel
              sx={{
                width: { md: 270 },
              }}
            >
              Cost Centers
            </InputLabel>

            <Select
              fullWidth
              id="cost-center"
              name="cost_centre_id"
              value={formData.cost_centre_id || ''}
              onChange={handleChange('cost_centre_id')}
              displayEmpty
              size="small"
            >
              {!user && (
                <MenuItem value="" disabled>
                  Select One
                </MenuItem>
              )}
              {formState.centres.map((centre) => (
                <MenuItem
                  key={centre.cost_centre_id}
                  value={centre.cost_centre_id}
                >
                  {centre.cost_centre_name === 'NotAssigned'
                    ? 'No Cost Centre Assigned'
                    : centre.cost_centre_name}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          {/* Max SMS Per Day */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ md: 'center' }}
          >
            <InputLabel
              sx={{
                width: { md: 270 },
              }}
              required
            >
              Max SMS Per Day
            </InputLabel>
            <TextField
              fullWidth
              id="max-sms"
              name="max_sms_per_day"
              required
              type="number"
              value={formData.max_sms_per_day}
              onChange={handleChange('max_sms_per_day')}
              size="small"
              error={!!errors.max_sms_per_day}
              helperText={errors.max_sms_per_day}
            />
          </Stack>

          {user && (
            <>
              {/* Message Priority */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: { md: 270 },
                    display: 'flex',
                    gap: 1,
                  }}
                >
                  Message Priority
                  <HelpTooltip
                    message={<Typography>Message Priority Info</Typography>}
                  />
                </InputLabel>

                <Select
                  fullWidth
                  id="message-priority"
                  name="message_priority"
                  value={formData.message_priority || '0'}
                  onChange={handleChange('message_priority')}
                  size="small"
                  disabled={!isGodAdmin}
                >
                  <MenuItem value="0">Low</MenuItem>
                  <MenuItem value="1">Medium</MenuItem>
                  <MenuItem value="2">High</MenuItem>
                </Select>
              </Stack>

              {/* Receipt Flag */}
              <Box>
                <FormControlLabel
                  sx={{
                    margin: 0,
                    '& .MuiCheckbox-root': {
                      marginLeft: { md: 8.75 },
                    },
                  }}
                  name="reciept_flag"
                  control={
                    <Checkbox
                      checked={formData.reciept_flag || false}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          reciept_flag: e.target.checked,
                        }));
                      }}
                    />
                  }
                  label={
                    <Box display="flex" gap={1}>
                      Receipt Flag
                      <HelpTooltip
                        message={<Typography>Receipt Flag Info</Typography>}
                      />
                    </Box>
                  }
                  labelPlacement={mdAndUp ? 'start' : 'end'}
                />
              </Box>

              {/* HTTP Reply Flag */}
              <Box>
                <FormControlLabel
                  sx={{
                    margin: 0,
                    '& .MuiCheckbox-root': {
                      marginLeft: { md: 4.9 },
                    },
                  }}
                  name="http_reply_flag"
                  control={
                    <Checkbox
                      checked={formData.http_reply_flag || false}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          http_reply_flag: e.target.checked,
                        }));
                      }}
                    />
                  }
                  label={
                    <Box display="flex" gap={1}>
                      HTTP Reply Flag
                      <HelpTooltip
                        message={<Typography>HTTP Reply Flag Info</Typography>}
                      />
                    </Box>
                  }
                  labelPlacement={mdAndUp ? 'start' : 'end'}
                />
              </Box>

              {/* Message Threading */}
              <Box>
                <FormControlLabel
                  sx={{
                    margin: 0,
                    '& .MuiCheckbox-root': {
                      marginLeft: { md: 2 },
                    },
                  }}
                  name="last_sms_text_flag"
                  control={
                    <Checkbox
                      checked={formData.last_sms_text_flag || false}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          last_sms_text_flag: e.target.checked,
                        }));
                      }}
                    />
                  }
                  label={
                    <Box display="flex" gap={1}>
                      Message Threading
                      <HelpTooltip
                        message={<Typography>Message Priority Info</Typography>}
                      />
                    </Box>
                  }
                  labelPlacement={mdAndUp ? 'start' : 'end'}
                />
              </Box>
            </>
          )}

          {accessLevel === '3' && accessType === '3' && user && (
            <Box>
              <FormControlLabel
                sx={{
                  margin: 0,
                  '& .MuiCheckbox-root': {
                    marginLeft: { md: 8.5 },
                  },
                }}
                name="show_password"
                control={
                  <Checkbox
                    onChange={() => setShowPassword(!showPassword)}
                    checked={showPassword}
                  />
                }
                label="Show Password"
                labelPlacement={mdAndUp ? 'start' : 'end'}
              />
            </Box>
          )}

          {/* Password Section */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ md: 'center' }}
          >
            <InputLabel
              sx={{
                width: { md: 270 },
                whiteSpace: 'normal',
                marginTop: { md: '6px' },
              }}
              required
            >
              Set Password
            </InputLabel>
            <TextField
              fullWidth
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password || ''}
              onChange={handleChange('password')}
              size="small"
              error={!!errors.password}
              helperText={errors.password}
            />
          </Stack>

          {/* Confirm Password */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ md: 'center' }}
          >
            <InputLabel
              sx={{
                width: { md: 270 },
                whiteSpace: 'normal',
                marginTop: { md: '6px' },
              }}
              required
            >
              Confirm Password
            </InputLabel>
            <TextField
              fullWidth
              id="confirm-password"
              name="password_check"
              type={showPassword ? 'text' : 'password'}
              value={formData.password_check || ''}
              onChange={handleChange('password_check')}
              size="small"
              error={!!errors.password_check}
              helperText={errors.password_check}
            />
          </Stack>
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
        <Stack
          sx={{
            flex: 1,
            justifyContent: 'end',
            maxWidth: { md: 250 },
            marginLeft: { md: 23, lg: 0 },
          }}
        >
          <PasswordRequirements />
        </Stack>
      </Grid>

      <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
        <Stack spacing={3}>
          {user && (
            <>
              <Box>
                <FormControlLabel
                  sx={{
                    margin: 0,
                    '& .MuiCheckbox-root': {
                      marginLeft: { md: 5.75 },
                    },
                  }}
                  name="is_forward"
                  control={
                    <Checkbox
                      disabled={!isGodAdmin}
                      checked={formData.is_forward}
                      onChange={handleChange('is_forward')}
                    />
                  }
                  label={
                    <Box display="flex" gap={1}>
                      Call Forwarding
                      <HelpTooltip
                        message={
                          <Typography>
                            Redirects an incoming call from the assigned
                            dedicated numbers to your set To number.
                          </Typography>
                        }
                      />
                    </Box>
                  }
                  labelPlacement={mdAndUp ? 'start' : 'end'}
                />
              </Box>

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ md: 'center' }}
              >
                <InputLabel
                  sx={{
                    width: { md: 270 },
                  }}
                >
                  Forward Calls To
                </InputLabel>

                <TextField
                  fullWidth
                  id="forward_calls_to"
                  name="forward_calls_to"
                  required
                  value={formData.forward_calls_to}
                  onChange={handleChange('forward_calls_to')}
                  disabled={!formData.is_forward || !isGodAdmin}
                  size="small"
                />
              </Stack>
            </>
          )}
        </Stack>
      </Grid>
      <Grid size={12}>{generateUserInfo()}</Grid>

      {/* Error Summary */}
      {errors && Object.keys(errors).length > 0 && (
        <Grid size={12}>
          <ErrorList errors={Object.values(errors)} />
        </Grid>
      )}

      {/* General Error Display */}
      {generalError && (
        <Grid size={12}>
          <Alert severity="error">{generalError}</Alert>
        </Grid>
      )}

      <Grid size={12} sx={{ maxWidth: { md: 450 } }}>
        {/* Action Buttons */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ marginY: 4 }}
        >
          <Button
            variant="link"
            color="secondary"
            onClick={() => onClose(false)}
            disabled={formState.isSaving}
          >
            {UsersFormattedMessages.modalCancel}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSubmit}
            disabled={formState.isSaving}
          >
            {formState.isSaving
              ? UsersFormattedMessages.saving
              : UsersFormattedMessages.saveChanges}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default NewUserEditForm;
